/**
 * Client helpers for the Square ordering endpoint + Web Payments SDK loader.
 */

// REACT_APP_API_BASE overrides the API origin (e.g. a local API server on a
// custom port); otherwise dev talks to the local server and prod to Vercel.
const API_BASE = process.env.REACT_APP_API_BASE
  || (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : 'https://my-app-ramakrishna5s-projects.vercel.app');

// Web Payments SDK per Square environment (must match the backend order env).
const SQUARE_SDK_URLS = {
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
  production: 'https://web.squarecdn.com/v1/square.js',
};

// Which Square environment the menu's catalog IDs came from. Must match the
// order environment for catalog-linked line items; otherwise the backend falls
// back to ad-hoc pricing automatically.
export const CATALOG_ENV = process.env.REACT_APP_SQUARE_MENU_ENV || 'production';

export const MONEY = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

/** A failed order call as an Error carrying Square's error code (if any). */
function orderError(json, fallback) {
  const err = new Error(json.error || fallback);
  err.code = json.squareErrors?.[0]?.code || null;
  return err;
}
export const fmt$ = (dollars) => `$${(Number(dollars) || 0).toFixed(2)}`;

/** Create the order and return its tax-inclusive total without charging. */
export async function quoteOrder(payload) {
  const res = await fetch(`${API_BASE}/api/square-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, quote: true, catalogEnv: CATALOG_ENV }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw orderError(json, `Could not price the order (${res.status})`);
  return json; // { orderId, totalMoney, taxMoney, pickupAt, lines }
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

/**
 * Charge a previously quoted order with a payment token (card or wallet).
 *
 * If the connection drops we don't know whether the charge landed, so the same
 * request is sent again — the server keys the payment on order + token, and
 * reports an already-paid order as paid, so a retry can never charge twice.
 */
export async function payQuotedOrder({ orderId, sourceId, pickupName }, { retries = 2 } = {}) {
  let res;
  for (let attempt = 0; ; attempt++) {
    try {
      res = await fetch(`${API_BASE}/api/square-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, sourceId, pickupName }),
      });
      break;
    } catch (e) {
      if (attempt >= retries) throw new Error('We lost the connection while paying. Check your signal and tap Pay again — you won\'t be charged twice.');
      await sleep(900 * (attempt + 1));
    }
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw orderError(json, `Payment failed (${res.status})`);
  return json;
}

/** Live progress of one online order: { stage, code, paid, payAtStore, pickupAt, … }. */
export async function getOrderStatus(orderId) {
  const res = await fetch(`${API_BASE}/api/square-order?orderId=${encodeURIComponent(orderId)}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw new Error(json.error || `Could not load the order (${res.status})`);
  // An older API build answers this URL with the payment config, not a stage.
  if (!json.stage) throw new Error('Order tracking is not available yet');
  return json;
}

/**
 * Staff-only: 86 / restock an item for online ordering. Square's POS
 * sold-out flag is read-only to apps, so this writes the online-channel
 * overlay (Firestore) that the menu endpoint merges in — POS sold-outs
 * still flow through separately. Requires a Firebase ID token.
 */
export async function setItemAvailability({ itemId, itemName = null, soldOut, idToken }) {
  const res = await fetch(`${API_BASE}/api/square-order-admin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ action: 'availability', itemId, itemName, soldOut, env: CATALOG_ENV }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw new Error(json.error || `Could not update availability (${res.status})`);
  return json;
}

/** Staff-only: today's online orders for the management panel. */
export async function adminListOrders(idToken) {
  const res = await fetch(`${API_BASE}/api/square-order-admin?env=${encodeURIComponent(CATALOG_ENV)}`, {
    headers: { 'Authorization': `Bearer ${idToken}` },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw new Error(json.error || `Could not load orders (${res.status})`);
  return json.orders;
}

/**
 * Staff-only order mutation. action: 'fulfillment' | 'cancel' | 'edit-items'
 * plus the fields that action needs (state / reason / updates / removes / adds).
 */
export async function adminOrderAction(payload, idToken) {
  const res = await fetch(`${API_BASE}/api/square-order-admin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ env: CATALOG_ENV, ...payload }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw new Error(json.error || `Order update failed (${res.status})`);
  return json;
}

/** Staff Menu Manager: fetch the online-catalog overlay ({ items, categories }). */
export async function adminGetCatalogOverlay(idToken) {
  const res = await fetch(`${API_BASE}/api/square-catalog-admin?env=${encodeURIComponent(CATALOG_ENV)}`, {
    headers: { 'Authorization': `Bearer ${idToken}` },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw new Error(json.error || `Could not load menu overrides (${res.status})`);
  return json.overlay;
}

/**
 * Staff Menu Manager mutation. action: 'upsert-item' | 'reset-item' |
 * 'upsert-category' | 'reorder' | 'reorder-categories' | 'upload-photo' |
 * 'remove-photo' plus that action's fields.
 */
export async function adminCatalogAction(payload, idToken) {
  const res = await fetch(`${API_BASE}/api/square-catalog-admin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ env: CATALOG_ENV, ...payload }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw new Error(json.error || `Menu update failed (${res.status})`);
  return json;
}

/**
 * Resize + compress an image File in the browser to a JPEG data URL that stays
 * under ~600 KB, so uploads are fast and fit the Firestore-backed photo store.
 * Downscales to `maxDim` on the long edge and steps quality down as needed.
 */
export async function compressImageFile(file, maxDim = 1000) {
  if (!file || !file.type?.startsWith('image/')) throw new Error('Please choose an image file.');
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('That image could not be loaded.'));
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';           // flatten transparency onto white for JPEG
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  let quality = 0.85, out = canvas.toDataURL('image/jpeg', quality);
  // base64 is ~1.34× the byte size; keep the decoded image well under 800 KB.
  while (out.length > 600 * 1024 * 1.34 && quality > 0.4) {
    quality -= 0.1;
    out = canvas.toDataURL('image/jpeg', quality);
  }
  return out;
}

/** Fetch publishable client config (appId, locationId) for the SDK. */
export async function getPaymentConfig() {
  const res = await fetch(`${API_BASE}/api/square-order`);
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || 'Payment config unavailable');
  return json; // { appId, locationId, environment }
}

/** Submit the cart to create an order (pay-at-store, or card via sourceId). */
export async function submitOrder(payload) {
  const res = await fetch(`${API_BASE}/api/square-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ catalogEnv: CATALOG_ENV, ...payload }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) throw orderError(json, `Order failed (${res.status})`);
  return json;
}

/** Inject the Square Web Payments SDK script once; resolves with window.Square. */
let _sdkPromise = null;
export function loadSquareSdk(environment = 'sandbox') {
  if (window.Square) return Promise.resolve(window.Square);
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SQUARE_SDK_URLS[environment] || SQUARE_SDK_URLS.sandbox;
    s.async = true;
    s.onload = () => window.Square ? resolve(window.Square) : reject(new Error('Square SDK failed to initialize'));
    s.onerror = () => { _sdkPromise = null; reject(new Error('Could not load Square payment library')); };
    document.head.appendChild(s);
  });
  return _sdkPromise;
}
